import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, LinearProgress, Chip, Alert } from '@mui/material';
import { getMyTierStatus, getUserTierStatus } from '../../api/tierApi';

const PP = "'Poppins', sans-serif";

const BADGE_PALETTE = {
  slate:   { bg: '#F1F5F9', fg: '#475569' },
  bronze:  { bg: '#FEF3C7', fg: '#92400E' },
  silver:  { bg: '#E5E7EB', fg: '#374151' },
  gold:    { bg: '#FEF3C7', fg: '#B45309' },
  diamond: { bg: '#DBEAFE', fg: '#1D4ED8' },
};

function fmtRm(v) {
  const n = Number(v) || 0;
  if (n >= 1_000_000) return `RM ${(Math.round(n / 100_000) / 10).toString().replace(/\.0$/, '')}mil`;
  if (n >= 1_000)     return `RM ${Math.round(n / 1_000)}k`;
  return `RM ${Math.round(n).toLocaleString()}`;
}

export default function TierCard({ userId = null, compact = false }) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetcher = userId ? getUserTierStatus(userId) : getMyTierStatus();
    fetcher
      .then(r => setStatus(r.data))
      .catch(e => setError(e.response?.data ?? e.message));
  }, [userId]);

  if (error) return <Alert severity="warning" sx={{ borderRadius: '10px' }}>{String(error)}</Alert>;
  if (!status) return null;

  const { currentTier, nextTier, accumulatedRevenue, revenueToNextTier, progressPct, pendingPromotion, isEligible } = status;
  const palette = BADGE_PALETTE[currentTier?.badgeColor] ?? BADGE_PALETTE.slate;

  // Hide entirely for non-Sales-Agent users (In-house Agents, Leaders, admins, etc.).
  if (isEligible === false) return null;

  if (!currentTier && !nextTier) {
    return (
      <Card sx={{ borderRadius: '14px' }}>
        <CardContent sx={{ p: '14px !important' }}>
          <Typography sx={{ fontSize: '0.85rem', color: '#64748B', fontFamily: PP }}>
            Tier ladder not yet configured.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ borderRadius: '14px' }}>
      <CardContent sx={{ p: compact ? '14px !important' : '20px !important' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Typography sx={{ fontSize: compact ? '0.8rem' : '0.95rem', fontWeight: 700, color: '#0F172A', fontFamily: PP }}>
            Sales Ranking
          </Typography>
          {currentTier && (
            <Chip
              label={currentTier.name}
              size="small"
              sx={{ backgroundColor: palette.bg, color: palette.fg, fontWeight: 700, fontFamily: PP }}
            />
          )}
        </Box>
        <Typography sx={{ fontSize: '0.8rem', color: '#64748B', fontFamily: PP }}>
          Revenue (since 1 May 2026): <strong>{fmtRm(accumulatedRevenue)}</strong>
        </Typography>

        {nextTier ? (
          <Box sx={{ mt: 1.5 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
              <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: PP }}>
                Next: <strong>{nextTier.name}</strong> at {fmtRm(nextTier.revenueThreshold)}
              </Typography>
              <Typography sx={{ fontSize: '0.72rem', color: '#1A8CFF', fontWeight: 600, fontFamily: PP }}>
                {fmtRm(revenueToNextTier)} to go
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, Number(progressPct) || 0)}
              sx={{ height: 8, borderRadius: 4, backgroundColor: '#E2E8F0',
                    '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: '#1A8CFF' } }}
            />
          </Box>
        ) : (
          <Box sx={{ mt: 1.5 }}>
            <Typography sx={{ fontSize: '0.78rem', color: '#10B981', fontWeight: 600, fontFamily: PP, mb: 0.75 }}>
              Top tier reached.
            </Typography>
            <LinearProgress
              variant="determinate"
              value={100}
              sx={{ height: 8, borderRadius: 4, backgroundColor: '#E2E8F0',
                    '& .MuiLinearProgress-bar': { borderRadius: 4, backgroundColor: '#10B981' } }}
            />
          </Box>
        )}
        <Typography sx={{ mt: 1, fontSize: '0.66rem', color: '#94A3B8', fontFamily: PP }}>
          Counts system price after discount (excludes add-ons); co-broke counts 50%.
        </Typography>

        {pendingPromotion && (() => {
          const s = pendingPromotion.status;
          if (s === 'Pending') {
            return (
              <Alert severity="warning" sx={{ mt: 1.5, fontFamily: PP, fontSize: '0.78rem', borderRadius: '8px' }}>
                Promotion to <strong>{pendingPromotion.toTierName}</strong> ({pendingPromotion.toTierPscPercent}%) — pending Accountant approval.
              </Alert>
            );
          }
          if (s === 'AccountantApproved') {
            return (
              <Alert severity="info" sx={{ mt: 1.5, fontFamily: PP, fontSize: '0.78rem', borderRadius: '8px' }}>
                Promotion to <strong>{pendingPromotion.toTierName}</strong> ({pendingPromotion.toTierPscPercent}%) — pending Super Admin final approval.
              </Alert>
            );
          }
          if (s === 'Rejected') {
            return (
              <Alert severity="error" sx={{ mt: 1.5, fontFamily: PP, fontSize: '0.78rem', borderRadius: '8px' }}>
                Last promotion attempt to <strong>{pendingPromotion.toTierName}</strong> was <strong>rejected</strong>. Current commission rates unchanged.
                {pendingPromotion.rejectionReason && (
                  <Box sx={{ mt: 0.5, fontStyle: 'italic', color: '#7F1D1D' }}>Reason: {pendingPromotion.rejectionReason}</Box>
                )}
              </Alert>
            );
          }
          if (s === 'Approved') {
            return (
              <Alert severity="success" sx={{ mt: 1.5, fontFamily: PP, fontSize: '0.78rem', borderRadius: '8px' }}>
                Promoted to <strong>{pendingPromotion.toTierName}</strong> on {pendingPromotion.effectiveFrom?.slice(0, 10)}.
              </Alert>
            );
          }
          return null;
        })()}
      </CardContent>
    </Card>
  );
}
